import { useCallback, useEffect, useState } from "react";
import { SelectChangeEvent } from "@mui/material";
import { ITopicPair } from "src/app/nlp/~store/models/TopicPair";
import { Edge, Node } from "reactflow";
import { TopicPairsTree } from "../models/common";
import {
    findAllRootTopicPairs,
    makeGraphData,
    makeTopicPairsTrees,
} from "../utils/graphDataCreation";
import { START_DIALOG_TRIGGER_NAME } from "../constants/common";

export interface UseGraphProps {
    topicPairs: ITopicPair[];
}

export interface UseGraph {
    treesNames: { label: string; subject: string; value: number }[];
    nodes: Node[];
    edges: Edge[];
    selectedPairId?: number;
    selectTree: (
        event: SelectChangeEvent<string>,
        child: React.ReactNode
    ) => void;
}

const useGraph = ({ topicPairs }: UseGraphProps): UseGraph => {
    const [rootPairs, setRootPairs] = useState<ITopicPair[]>([]);
    const [allTrees, setAllTrees] = useState<TopicPairsTree[]>([]);
    const [selectedPairId, setSelectedPairId] = useState<number>();
    const [nodes, setNodes] = useState<Node[]>([]);
    const [edges, setEdges] = useState<Edge[]>([]);

    const selectTree = useCallback((id: number, tree?: TopicPairsTree) => {
        setSelectedPairId(id);
        if (tree) {
            const { edges, nodes } = makeGraphData(tree);
            setEdges(edges);
            setNodes(nodes);
        }
    }, []);

    const handleSelectTreeEvent = useCallback(
        (event: SelectChangeEvent<string>) => {
            const selectedId = Number(event.target.value ?? 0);
            const tree = allTrees.find(
                (tree) => tree.pair.topicPairId === selectedId
            );
            selectTree(selectedId, tree);
        },
        [allTrees, selectTree]
    );

    useEffect(() => {
        if (topicPairs.length) {
            const allRootTopicPairs = findAllRootTopicPairs(topicPairs);
            const trees = makeTopicPairsTrees(topicPairs);
            const startParamsDialogPair = allRootTopicPairs.find(
                (pair) => pair.triggerName === START_DIALOG_TRIGGER_NAME
            );
            const selectedId =
                startParamsDialogPair?.topicPairId ??
                allRootTopicPairs?.[0]?.topicPairId;
            const tree = trees.find(
                (tree) => tree.pair.topicPairId === selectedId
            );
            setRootPairs(allRootTopicPairs);
            setAllTrees(trees);
            selectTree(selectedId, tree);
        }
    }, [selectTree, topicPairs]);

    return {
        treesNames: rootPairs.map((pair) => ({
            label: pair.triggerName,
            subject: pair.subject,
            value: pair.topicPairId,
        })),
        selectedPairId,
        nodes,
        edges,
        selectTree: handleSelectTreeEvent,
    };
};

export default useGraph;
